// Drafts (pages/Applications.tsx): jobs saved to apply to, grouped by how far
// along the written part is, closest deadline first.
//
//   users/{uid}/writtenApplications/{id}    a saved job (Draft below)
//   users/{uid}/applicationQuestions/{id}   its questions and answers
//   users/{uid}/roleActions/{roleId}        roles saved from New roles on Home, shown here too
//
// "Submit" moves a job to Applications: a tracker card (draftCard, saved by
// backend/app.py role_applied) and the draft marked completed.

import { normalizeCompany } from "./dreamCompanies";
import type { RoleAction } from "./newRoles";
import { localDate } from "./newRoles";

export type DraftStatus = "saved" | "in-progress" | "completed";
export type DraftSource = "pasted" | "feed" | "manual";

export type Draft = {
  id: string;
  company: string;
  role: string;
  jobUrl?: string;
  status: DraftStatus;
  createdAt: any;
  /** yyyy-mm-dd */
  deadline?: string;
  location?: string;
  /** The company's website and logo, for its icon (from the link lookup or a Home save) */
  domain?: string;
  logo?: string;
  /** Older drafts have none: added by hand */
  source?: DraftSource;
  /** Started from a New roles save: its roleActions id */
  roleId?: string;
  /** ISO, set by Submit */
  appliedAt?: string;
  cardId?: string;
};

export type DraftQuestion = {
  id: string;
  applicationId: string;
  question: string;
  answer: string;
  wordLimit?: number;
  order: number;
};

export type DraftStage = "drafting" | "ready" | "saved";

export const STAGES: { id: DraftStage; title: string; color: string; hint: string }[] = [
  { id: "drafting", title: "Drafting", color: "#D98829", hint: "Answers started" },
  { id: "ready", title: "Ready to submit", color: "#8A4B22", hint: "Every answer written" },
  { id: "saved", title: "Saved", color: "#D9CFC6", hint: "Not started" },
];

/** One row: a draft, or a role saved on Home that hasn't been started. */
export type DraftRow = {
  key: string;
  draft?: Draft;
  saved?: RoleAction;
  company: string;
  role: string;
  url?: string;
  deadline?: string;
  location?: string;
  domain?: string;
  logo?: string;
  source: DraftSource;
  /** ms, for "Oct 6" and ties */
  savedAt: number;
  stage: DraftStage;
  total: number;
  answered: number;
};

const DAY_MS = 24 * 3600000;

/** Firestore Timestamp, Date, ISO string or ms -> ms (0 when unknown) */
export function toMs(value: any): number {
  if (!value) return 0;
  if (typeof value === "number") return value;
  if (typeof value === "string") return Date.parse(value) || 0;
  if (value instanceof Date) return value.getTime();
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.seconds === "number") return value.seconds * 1000;
  return 0;
}

export const isAnswered = (q: DraftQuestion) => Boolean(q.answer?.trim());

export function stageOf(draft: Draft, questions: DraftQuestion[]): DraftStage {
  if (draft.status === "saved") return "saved";
  return questions.length > 0 && questions.every(isAnswered) ? "ready" : "drafting";
}

/** "2026-10-17" -> local midnight ms */
function dayMs(ymd: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd || "");
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getTime() : null;
}

/** Whole days from today to the deadline (negative once it's passed), null without one. */
export function daysLeft(deadline: string | undefined, now = new Date()): number | null {
  const due = dayMs(deadline || "");
  if (due === null) return null;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  return Math.round((due - today) / DAY_MS);
}

/** "Closes today", "Closes tomorrow", "Closes Fri" (this week), "Closes Oct 30", "Closed" */
export function deadlineLabel(deadline: string | undefined, now = new Date()): string | null {
  const days = daysLeft(deadline, now);
  if (days === null) return null;
  if (days < 0) return "Closed";
  if (days === 0) return "Closes today";
  if (days === 1) return "Closes tomorrow";
  const due = new Date(dayMs(deadline!)!);
  if (days < 7) return `Closes ${due.toLocaleDateString("en-US", { weekday: "short" })}`;
  return `Closes ${due.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
}

/** Inside 3 days: the row is tinted and the chip warmer. */
export const isUrgent = (deadline: string | undefined, now = new Date()) => {
  const days = daysLeft(deadline, now);
  return days !== null && days >= 0 && days <= 3;
};

/** "2 of 3 written", "All 3 written", "3 questions", or null when none are added yet */
export function effortLabel(row: Pick<DraftRow, "stage" | "total" | "answered">): string | null {
  if (!row.total) return null;
  if (row.stage === "saved") return `${row.total} question${row.total === 1 ? "" : "s"}`;
  if (row.answered === row.total) return `All ${row.total} written`;
  return `${row.answered} of ${row.total} written`;
}

/** "Oct 6" */
export const shortDate = (ms: number) =>
  ms ? new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";

/** Closest deadline first, no deadline after; then the newest saved. */
export function byDeadline(a: DraftRow, b: DraftRow): number {
  const da = dayMs(a.deadline || "");
  const db = dayMs(b.deadline || "");
  if (da !== null && db !== null && da !== db) return da - db;
  if ((da === null) !== (db === null)) return da === null ? 1 : -1;
  return b.savedAt - a.savedAt;
}

export function matchesSearch(row: DraftRow, term: string): boolean {
  const t = term.trim().toLowerCase();
  return !t || row.company.toLowerCase().includes(t) || row.role.toLowerCase().includes(t);
}

/**
 * The rows for each stage. Completed drafts aren't here (they're in
 * Applications); a saved role already started as a draft shows once, as the draft.
 */
export function groupRows(
  drafts: Draft[],
  questions: DraftQuestion[],
  savedRoles: RoleAction[],
  term = "",
): Record<DraftStage, DraftRow[]> {
  const byApp = new Map<string, DraftQuestion[]>();
  for (const q of questions) {
    const list = byApp.get(q.applicationId);
    if (list) list.push(q);
    else byApp.set(q.applicationId, [q]);
  }
  const started = new Set(drafts.map((d) => d.roleId).filter(Boolean));
  const rows: DraftRow[] = [];

  for (const draft of drafts) {
    if (draft.status === "completed") continue;
    const qs = byApp.get(draft.id) || [];
    rows.push({
      key: draft.id,
      draft,
      company: draft.company,
      role: draft.role || "",
      url: draft.jobUrl,
      deadline: draft.deadline,
      location: draft.location,
      domain: draft.domain,
      logo: draft.logo,
      source: draft.source || "manual",
      savedAt: toMs(draft.createdAt),
      stage: stageOf(draft, qs),
      total: qs.length,
      answered: qs.filter(isAnswered).length,
    });
  }
  for (const saved of savedRoles) {
    if (saved.status !== "saved" || started.has(saved.id)) continue;
    rows.push({
      key: `feed:${saved.id}`,
      saved,
      company: saved.company,
      role: saved.title,
      url: saved.url,
      location: saved.location || undefined,
      domain: saved.domain || undefined,
      logo: saved.logo || undefined,
      source: "feed",
      savedAt: toMs(saved.at),
      stage: "saved",
      total: 0,
      answered: 0,
    });
  }

  const groups: Record<DraftStage, DraftRow[]> = { drafting: [], ready: [], saved: [] };
  for (const row of rows) if (matchesSearch(row, term)) groups[row.stage].push(row);
  Object.values(groups).forEach((list) => list.sort(byDeadline));
  return groups;
}

/**
 * The website and logo for a row's icon: its own, else the Dream Company with
 * that name (`dreamDomains`: normalized name -> domain).
 */
export function brandOf(
  item: { company: string; domain?: string; logo?: string },
  dreamDomains: Map<string, string>,
): { domain?: string; logo?: string } {
  return { domain: item.domain || dreamDomains.get(normalizeCompany(item.company)), logo: item.logo };
}

/** Drafts submitted in the last 7 days */
export function movedThisWeek(drafts: Draft[], now = Date.now()): number {
  return drafts.filter((d) => d.status === "completed" && now - toMs(d.appliedAt) <= 7 * DAY_MS).length;
}

/** Doc id for the tracker card Submit creates: one per draft, so a double click can't make two */
export const draftCardId = (draft: Pick<Draft, "id">) => `draft_${draft.id}`;

/**
 * The Applied card for Submit (same shape as newRoles.applicationFromRole).
 * Gmail sync merges the confirmation email into it later.
 */
export function draftCard(draft: Draft, now = new Date()) {
  return {
    message_id: draftCardId(draft),
    company: draft.company,
    role: draft.role,
    label: "submitted",
    score: 1.0,
    date: localDate(now),
    lctn: draft.location || "",
    body: "Added from Drafts",
    notes: "",
    manual_entry: true,
    source: "drafts",
    ...(draft.roleId ? { feedRoleId: draft.roleId } : {}),
    ...(draft.jobUrl ? { jobUrl: draft.jobUrl } : {}),
    archiveId: null,
    history: [{ stage: "submitted", at: now.toISOString(), source: "seed" }],
  };
}

/** The writtenApplications doc for starting a role saved on Home */
export function draftFromSaved(saved: RoleAction) {
  return {
    company: saved.company,
    role: saved.title,
    ...(saved.url ? { jobUrl: saved.url } : {}),
    ...(saved.location ? { location: saved.location } : {}),
    ...(saved.domain ? { domain: saved.domain } : {}),
    ...(saved.logo ? { logo: saved.logo } : {}),
    source: "feed" as DraftSource,
    roleId: saved.id,
    status: "in-progress" as DraftStatus,
  };
}
